import { supabase } from "../lib/supabase";
import { fetchProfileCards } from "./profileCardService";
import { enrollmentService } from "./enrollmentService";
import { analyticsService } from "./analyticsService";
import { getCurrentPlatform } from "../utils/platform";
import { authService } from "./authService";
import { notificationQueueService } from "./notificationQueueService";
import { lessonOrderService } from "./lessonOrderService";
import {
  createLessonMutationService,
  createQuizAnswerKeyService,
  mergeQuizAnswerKeys,
  splitQuizAnswerKeys,
} from "./lessonMutationService";
import { resolveLessonModuleId } from "./lessonModuleService";
import { createCourseDeletionService } from "./courseDeletionService";
import { duplicateCourseWithContent } from "./courseDuplicationService";
import { createCourseBulkMutationService } from "./courseBulkMutationService";
import { ensureAffectedRows, unwrapSupabaseResult } from "./supabaseResult";
import { withCourseAccreditationRule } from "../utils/courseAccreditation";
import { getCourseUpdateError } from "../utils/courseEditVersion";
import { uploadService } from "./uploadService";
import {
  buildCourseFilterOptions,
  COURSE_CATALOG_SELECT,
  COURSE_LOOKUP_SELECT,
} from "../utils/courseCatalog";
import { isCertificateNameMissingOrPlaceholder } from "../utils/certificateProfile";
import {
  excludeAnalyticsUsers,
  getAnalyticsTestUserIds,
} from "./analyticsFilterService";

const quizAnswerKeyService = createQuizAnswerKeyService(supabase);

// Quiz answer keys are kept out of `lessons.quiz_data` (a database trigger
// strips them), so authoring reads merge them back from
// lesson_quiz_answer_keys — a table only admins and the course instructor can
// read. RLS returns no rows for anyone else, so their lessons come back as
// fetched; a failed load throws rather than handing back keyless questions the
// next save would write over.
const withQuizAnswerKeys = async (lessons) => {
  // Gated on the payload, not on lesson.type, to match the database trigger.
  const quizLessonIds = (lessons || [])
    .filter(
      (lesson) => lesson?.id && Array.isArray(lesson?.quiz_data?.questions),
    )
    .map((lesson) => lesson.id);
  if (quizLessonIds.length === 0) return lessons;

  const answerKeysByLesson = await quizAnswerKeyService.load(quizLessonIds);

  return lessons.map((lesson) => {
    const answerKeys = answerKeysByLesson[lesson?.id];
    if (!answerKeys) return lesson;
    return {
      ...lesson,
      quiz_data: mergeQuizAnswerKeys(lesson.quiz_data, answerKeys),
    };
  });
};

// Simple in-memory cache for getAllCourses to avoid redundant fetches
let _coursesCache = null;
let _coursesCacheTime = 0;
const CACHE_TTL = 60_000; // 60 seconds
let _courseLookupCache = null;
let _courseLookupCacheTime = 0;
let _courseFilterOptionsCache = null;
let _courseFilterOptionsCacheTime = 0;
const FILTER_OPTIONS_CACHE_TTL = 5 * 60_000;

const PUBLIC_CURRICULUM_SELECT = [
  "id",
  "course_id",
  "module_id",
  "title",
  "description",
  "duration_minutes",
  "order_index",
  "sort_order",
  "sequence_number",
  "is_mandatory",
  "type",
  "is_published",
].join(",");

// Only published lessons of the delivery mode the page sells are listed.
const getPublicCourseCurriculum = async (
  courseId,
  deliveryMode = "self_paced",
) => {
  const result = await supabase.rpc("get_public_course_curriculum", {
    p_course_id: courseId,
    p_delivery_mode: deliveryMode,
  });

  if (!result.error) return result;

  // Keep public course pages working when the frontend deploy reaches
  // production immediately before the database migration.
  if (!["PGRST202", "42883"].includes(result.error.code)) return result;

  return supabase
    .from("lessons")
    .select(PUBLIC_CURRICULUM_SELECT)
    .eq("course_id", courseId)
    .eq("delivery_mode", deliveryMode)
    .eq("is_published", true)
    .order("order_index", { ascending: true })
    .order("id", { ascending: true });
};

export const courseService = {
  // Instructor-provided assistant knowledge lives in course_ai_knowledge,
  // readable only by admins, the course's instructor and enrolled learners.
  async getCourseAiKnowledge(courseId) {
    if (!courseId) return "";
    const { data, error } = await supabase
      .from("course_ai_knowledge")
      .select("content")
      .eq("course_id", courseId)
      .maybeSingle();
    if (error) throw error;
    return data?.content || "";
  },

  // Get courses with pagination for admin dashboard
  async getAdminCourses({
    page = 1,
    limit = 10,
    search = "",
    filters = {},
    excludeTestAccounts = true,
    testAccountEmails,
  }) {
    try {
      const from = (page - 1) * limit;
      const to = from + limit - 1;

      // Start building the query
      let query = supabase.from("courses").select(
        `
          *,
          category:course_categories(id, name, slug)
        `,
        { count: "exact" },
      );

      // Apply search
      if (search) {
        query = query.or(
          `title.ilike.%${search}%,description.ilike.%${search}%`,
        );
      }

      // Apply filters
      if (filters.category) query = query.eq("category_id", filters.category);
      if (filters.status) query = query.eq("status", filters.status);
      if (filters.iso_standard)
        query = query.eq("iso_standard", filters.iso_standard);

      // Apply pagination and sorting
      const { data, error, count } = await query
        .order("created_at", { ascending: false })
        .range(from, to);

      if (error) throw error;

      const enrollments = await analyticsService.getReportingEnrollments({
        excludeTestAccounts,
        testAccountEmails,
      });
      const coursesWithStats = data.map((course) => {
        const rows = enrollments.filter((row) => row.course_id === course.id);
        return {
          ...course,
          total_students: new Set(rows.map((row) => row.user_id)).size,
          completion_rate: rows.length
            ? Math.round(
                (rows.filter((row) => row.enrollment_status === "completed")
                  .length /
                  rows.length) *
                  100,
              )
            : 0,
          averageRating: course.rating || 0,
          totalRatings: course.total_ratings || 0,
        };
      });

      return {
        courses: coursesWithStats,
        total: count,
        page,
        totalPages: Math.ceil(count / limit),
      };
    } catch (error) {
      console.error("Get admin courses error:", error);
      throw new Error("Failed to fetch courses");
    }
  },

  // Get catalog courses with only fields used by cards, cart suggestions, and filters.
  async getAllCourses() {
    // Return cached data if fresh
    if (_coursesCache && Date.now() - _coursesCacheTime < CACHE_TTL) {
      return _coursesCache;
    }
    try {
      let { data, error } = await supabase
        .from("courses")
        .select(COURSE_CATALOG_SELECT)
        .eq("status", "published")
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      // If relationship query fails, fallback to basic query
      if (error) {
        const basicSelect = COURSE_CATALOG_SELECT.split("category:")[0].replace(
          /,\s*$/,
          "",
        );
        const { data: basicData, error: basicError } = await supabase
          .from("courses")
          .select(basicSelect)
          .eq("status", "published")
          .eq("is_active", true)
          .order("created_at", { ascending: false });

        if (basicError) throw basicError;
        data = basicData;
      }

      // Use the pre-computed total_enrollments column from courses table
      // This eliminates the N+1 query pattern (was 44 queries, now 1)
      const coursesWithStats = (data || []).map((course) => ({
        ...course,
        total_students: course.total_enrollments || 0,
        completion_rate: 0,
        averageRating: course.rating || 0,
        totalRatings: course.total_ratings || 0,
      }));

      _coursesCache = coursesWithStats;
      _coursesCacheTime = Date.now();
      return coursesWithStats;
    } catch (error) {
      console.error("Get all courses error:", error);
      // Let callers tell "no courses" from "could not load courses"; every
      // caller handles the rejection.
      throw error;
    }
  },

  // Invalidate the courses cache (call after course mutations)
  invalidateCoursesCache() {
    _coursesCache = null;
    _coursesCacheTime = 0;
    _courseLookupCache = null;
    _courseLookupCacheTime = 0;
    _courseFilterOptionsCache = null;
    _courseFilterOptionsCacheTime = 0;
  },

  // Alias for getAllCourses to maintain compatibility with components
  async getCourses() {
    return this.getAllCourses();
  },

  async getCourseLookupList() {
    if (_courseLookupCache && Date.now() - _courseLookupCacheTime < CACHE_TTL) {
      return _courseLookupCache;
    }

    try {
      const { data, error } = await supabase
        .from("courses")
        .select(COURSE_LOOKUP_SELECT)
        .eq("status", "published")
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) throw error;

      _courseLookupCache = data || [];
      _courseLookupCacheTime = Date.now();
      return _courseLookupCache;
    } catch (error) {
      console.error("Get course lookup list error:", error);
      return [];
    }
  },

  // Get single course by ID with detailed information
  async getCourseById(courseId, options = {}) {
    try {
      const includeEnrollmentStats = options.includeEnrollmentStats !== false;
      // Callers that source their own curriculum (the enrolled course player
      // reads it from get_course_mode_player_context, which strips quiz answer
      // keys) opt out of the lesson fetch entirely so `lessons.*` — quiz_data
      // included — never reaches the browser.
      const includeLessons = options.includeLessons !== false;
      const lessonAccess =
        options.lessonAccess === "public"
          ? "public"
          : options.lessonAccess === "preview"
            ? "preview"
            : "full";
      const requestedDeliveryMode =
        options.deliveryMode ||
        (lessonAccess === "preview" ? "self_paced" : null);
      let lessonQuery = !includeLessons
        ? Promise.resolve({ data: [], error: null })
        : lessonAccess === "public"
          ? getPublicCourseCurriculum(
              courseId,
              requestedDeliveryMode || "self_paced",
            )
          : supabase
              .from("lessons")
              .select(
                lessonAccess === "preview" ? PUBLIC_CURRICULUM_SELECT : "*",
              )
              .eq("course_id", courseId)
              .order("order_index", { ascending: true })
              .order("id", { ascending: true });
      let moduleQuery =
        !includeLessons || lessonAccess === "public"
          ? Promise.resolve({ data: [], error: null })
          : supabase
              .from("course_modules")
              .select("id, title, sort_order")
              .eq("course_id", courseId)
              .order("sort_order", { ascending: true });
      if (
        includeLessons &&
        lessonAccess !== "public" &&
        requestedDeliveryMode
      ) {
        lessonQuery = lessonQuery.eq("delivery_mode", requestedDeliveryMode);
        moduleQuery = moduleQuery.eq("delivery_mode", requestedDeliveryMode);
      }
      const enrollmentQuery = includeEnrollmentStats
        ? supabase
            .from("course_enrollments")
            .select("id, enrollment_status")
            .eq("course_id", courseId)
            .eq("platform", getCurrentPlatform())
        : Promise.resolve({ data: [], error: null });

      const [courseResult, detailResults] = await Promise.all([
        supabase
          .from("courses")
          .select(
            `
          *,
          category:course_categories(
            id,
            name,
            slug
          )
          `,
          )
          .eq("id", courseId)
          .single(),
        Promise.allSettled([lessonQuery, moduleQuery, enrollmentQuery]),
      ]);
      let { data, error } = courseResult;

      // If relationship query fails, fallback to basic query
      if (error) {
        const { data: basicData, error: basicError } = await supabase
          .from("courses")
          .select("*")
          .eq("id", courseId)
          .single();

        if (basicError) throw basicError;
        data = basicData;
      }

      if (!data) throw new Error("Course not found");

      let lessons = [];
      let courseModules = [];
      let totalStudents = 0;
      let completionRate = 0;

      const [lessonResult, moduleResult, enrollmentResult] = detailResults;

      lessons = unwrapSupabaseResult(lessonResult, "course lessons");
      courseModules = unwrapSupabaseResult(moduleResult, "course modules");

      if (lessonAccess === "full") {
        lessons = await withQuizAnswerKeys(lessons);
      }

      if (lessonAccess === "public") {
        const publicModules = new Map();
        lessons.forEach((lesson) => {
          if (!lesson.module_id || publicModules.has(lesson.module_id)) return;
          publicModules.set(lesson.module_id, {
            id: lesson.module_id,
            title: lesson.module_title,
            sort_order: lesson.module_sort_order,
          });
        });
        courseModules = [...publicModules.values()];
      }

      if (includeEnrollmentStats) {
        const enrollments = unwrapSupabaseResult(
          enrollmentResult,
          "course enrollments",
        );
        totalStudents = enrollments?.length || 0;
        const completedStudents =
          enrollments?.filter((e) => e.enrollment_status === "completed")
            ?.length || 0;
        completionRate =
          totalStudents > 0
            ? Math.round((completedStudents / totalStudents) * 100)
            : 0;
      }

      // Group lessons into modules for syllabus display
      const modules = [];
      if (lessons.length > 0) {
        // Build a lookup from module_id to module metadata
        const moduleLookup = new Map();
        courseModules.forEach((m, i) => {
          moduleLookup.set(m.id, {
            title: m.title,
            sort_order: m.sort_order ?? i,
          });
        });

        // Group lessons by module_id
        const lessonsByModule = new Map();
        lessons.forEach((lesson) => {
          const key = lesson.module_id || "ungrouped";
          if (!lessonsByModule.has(key)) {
            lessonsByModule.set(key, []);
          }
          lessonsByModule.get(key).push(lesson);
        });

        // Sort module keys by their sort_order, ungrouped last
        const sortedKeys = [...lessonsByModule.keys()].sort((a, b) => {
          if (a === "ungrouped") return 1;
          if (b === "ungrouped") return -1;
          const orderA = moduleLookup.get(a)?.sort_order ?? 999;
          const orderB = moduleLookup.get(b)?.sort_order ?? 999;
          return orderA - orderB;
        });

        sortedKeys.forEach((key, index) => {
          const moduleLessons = lessonsByModule.get(key);
          const totalDuration = moduleLessons.reduce(
            (sum, l) => sum + (l.duration_minutes || 0),
            0,
          );
          const moduleTitle =
            moduleLookup.get(key)?.title || `Module ${index + 1}`;

          modules.push({
            id: key !== "ungrouped" ? key : undefined,
            title: moduleTitle,
            duration:
              totalDuration > 0
                ? `${Math.round(totalDuration / 60)} hrs`
                : undefined,
            summary: moduleLessons.map((l) => l.title).join(", "),
            lessons: moduleLessons,
          });
        });
      }

      return {
        ...data,
        lessons: lessons,
        modules: modules, // Add modules for syllabus display
        total_students: totalStudents,
        completion_rate: completionRate,
        total_duration: lessons.reduce(
          (sum, lesson) => sum + (lesson.duration_minutes || 0),
          0,
        ),
      };
    } catch (error) {
      console.error("Get course by ID error:", error);
      throw new Error("Failed to fetch course details");
    }
  },

  // Get filtered courses
  async getFilteredCourses(filters) {
    try {
      let query = supabase
        ?.from("courses")
        ?.select(
          `
          *,
          category:course_categories(
            id,
            name,
            slug
          )
        `,
        )
        ?.eq("status", "published")
        ?.eq("is_active", true);

      // Apply filters
      if (filters?.category) {
        query = query?.eq("category", filters?.category);
      }

      if (filters?.iso_standard) {
        query = query?.eq("iso_standard", filters?.iso_standard);
      }

      if (filters?.difficulty_level) {
        query = query?.eq("difficulty_level", filters?.difficulty_level);
      }

      if (filters?.min_price !== undefined) {
        query = query?.gte("price", filters?.min_price);
      }

      if (filters?.max_price !== undefined) {
        query = query?.lte("price", filters?.max_price);
      }

      if (filters?.min_duration) {
        query = query?.gte("duration_hours", filters?.min_duration);
      }

      if (filters?.max_duration) {
        query = query?.lte("duration_hours", filters?.max_duration);
      }

      if (filters?.search) {
        query = query?.or(
          `title.ilike.%${filters?.search}%,description.ilike.%${filters?.search}%`,
        );
      }

      const { data, error } = await query?.order("created_at", {
        ascending: false,
      });

      if (error) throw error;
      return data || [];
    } catch (error) {
      throw new Error("Failed to fetch filtered courses");
    }
  },

  // Check if user is enrolled in course
  async getUserEnrollment(courseId, userId) {
    return enrollmentService.getUserEnrollment(userId, courseId);
  },

  // Enroll user in course
  async enrollInCourse(
    courseId,
    userId,
    status = "pending",
    accreditationType = null,
    partnerId = null,
  ) {
    return enrollmentService.enrollInCourse(
      userId,
      courseId,
      status,
      null,
      accreditationType,
      partnerId,
    );
  },

  // Get course categories — uses dedicated lookup table (1 lightweight query)
  async getCategories({ allowFallback = true } = {}) {
    try {
      const { data, error } = await supabase
        .from("course_categories")
        .select("id, name, slug, sort_order, is_active")
        .eq("is_active", true)
        .order("name");

      if (error) throw error;
      if (!data?.length)
        return allowFallback ? this.getDefaultCategories() : [];

      return data;
    } catch (error) {
      console.error("Get categories error:", error);
      if (!allowFallback) throw error;
      return this.getDefaultCategories();
    }
  },

  // Get default categories (fallback)
  getDefaultCategories() {
    return [
      {
        id: "1",
        name: "Quality Management",
        description: "ISO 9001 and quality management system courses",
        slug: "quality-management",
        sort_order: 1,
        is_active: true,
      },
      {
        id: "2",
        name: "Environmental Management",
        description: "ISO 14001 and environmental management courses",
        slug: "environmental-management",
        sort_order: 2,
        is_active: true,
      },
      {
        id: "3",
        name: "Information Security",
        description: "ISO 27001 and information security courses",
        slug: "information-security",
        sort_order: 3,
        is_active: true,
      },
      {
        id: "4",
        name: "Occupational Health & Safety",
        description: "ISO 45001 and workplace safety courses",
        slug: "occupational-health-safety",
        sort_order: 4,
        is_active: true,
      },
      {
        id: "5",
        name: "Food Safety",
        description: "ISO 22000 and food safety management courses",
        slug: "food-safety",
        sort_order: 5,
        is_active: true,
      },
      {
        id: "6",
        name: "Professional Development",
        description:
          "General professional development and skill enhancement courses",
        slug: "professional-development",
        sort_order: 6,
        is_active: true,
      },
    ];
  },

  // Seed default categories if none exist
  async seedDefaultCategories() {
    try {
      const defaultCategories = [
        {
          name: "Quality Management",
          description: "ISO 9001 and quality management system courses",
          slug: "quality-management",
          sort_order: 1,
          is_active: true,
        },
        {
          name: "Environmental Management",
          description: "ISO 14001 and environmental management courses",
          slug: "environmental-management",
          sort_order: 2,
          is_active: true,
        },
        {
          name: "Information Security",
          description: "ISO 27001 and information security courses",
          slug: "information-security",
          sort_order: 3,
          is_active: true,
        },
        {
          name: "Occupational Health & Safety",
          description: "ISO 45001 and workplace safety courses",
          slug: "occupational-health-safety",
          sort_order: 4,
          is_active: true,
        },
        {
          name: "Food Safety",
          description: "ISO 22000 and food safety management courses",
          slug: "food-safety",
          sort_order: 5,
          is_active: true,
        },
        {
          name: "Professional Development",
          description:
            "General professional development and skill enhancement courses",
          slug: "professional-development",
          sort_order: 6,
          is_active: true,
        },
      ];

      // Try to insert categories one by one to handle duplicates
      for (const category of defaultCategories) {
        try {
          const { error } = await supabase
            .from("course_categories")
            .insert([category]);

          if (error && !error.message.includes("duplicate")) {
            console.error(`Error inserting category ${category.name}:`, error);
          }
        } catch (err) {
          console.error(`Error inserting category ${category.name}:`, err);
        }
      }
    } catch (error) {
      console.error("Seed categories error:", error);
      // Don't throw error to prevent breaking the main flow
    }
  },

  // Create course category (Admin only)
  async createCategory(categoryData) {
    try {
      const { data, error } = await supabase
        .from("course_categories")
        .insert([
          {
            name: categoryData.name,
            description: categoryData.description || null,
            slug:
              categoryData.slug ||
              categoryData.name.toLowerCase().replace(/\s+/g, "-"),
            sort_order: categoryData.sort_order || 0,
            is_active: true,
          },
        ])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Create category error:", error);
      throw new Error("Failed to create category");
    }
  },

  // Update course category (Admin only)
  async updateCategory(categoryId, categoryData) {
    try {
      const { data, error } = await supabase
        .from("course_categories")
        .update({
          name: categoryData.name,
          description: categoryData.description,
          slug: categoryData.slug,
          sort_order: categoryData.sort_order,
          is_active: categoryData.is_active,
        })
        .eq("id", categoryId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Update category error:", error);
      throw new Error("Failed to update category");
    }
  },

  // Delete course category (Admin only)
  async deleteCategory(categoryId) {
    try {
      // Check if category has courses
      const { data: courses } = await supabase
        .from("courses")
        .select("id")
        .eq("category_id", categoryId)
        .limit(1);

      if (courses && courses.length > 0) {
        throw new Error("Cannot delete category with associated courses");
      }

      const { error } = await supabase
        .from("course_categories")
        .delete()
        .eq("id", categoryId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error("Delete category error:", error);
      throw new Error(error.message || "Failed to delete category");
    }
  },

  // ===== ISO FRAMEWORKS MANAGEMENT =====

  // Get all ISO frameworks
  async getISOFrameworks() {
    try {
      const { data, error } = await supabase
        .from("iso_frameworks")
        .select("*")
        .order("name");

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Get ISO frameworks error:", error);
      throw new Error(error.message || "Failed to fetch ISO frameworks");
    }
  },

  // Get detailed ISO certifications/standards metadata
  async getISOCertifications() {
    try {
      const { data, error } = await supabase
        .from("iso_certifications")
        .select("*")
        .eq("is_active", true)
        .order("name");

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error fetching filtered courses:", error);
      throw error;
    }
  },

  // Get courses by framework name (searches tags and iso_frameworks)
  async getCoursesByFramework(frameworkName) {
    if (!frameworkName) return [];
    try {
      const { data, error } = await supabase
        .from("courses")
        .select("*")
        .eq("status", "published")
        .eq("is_active", true)
        .or(`tags.cs.{${frameworkName}},iso_frameworks.cs.{${frameworkName}}`);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error(
        `Error fetching courses for framework ${frameworkName}:`,
        error,
      );
      throw error;
    }
  },

  // Create new ISO framework
  async createISOFramework(frameworkData) {
    try {
      const { data, error } = await supabase
        .from("iso_frameworks")
        .insert([
          {
            name: frameworkData.name,
            code: frameworkData.code,
            description: frameworkData.description,
            is_active: frameworkData.is_active ?? true,
          },
        ])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error("Create ISO framework error:", error);
      throw new Error(error.message || "Failed to create ISO framework");
    }
  },

  // Update ISO framework
  async updateISOFramework(frameworkId, frameworkData) {
    try {
      const { data, error } = await supabase
        .from("iso_frameworks")
        .update({
          name: frameworkData.name,
          code: frameworkData.code,
          description: frameworkData.description,
          is_active: frameworkData.is_active,
        })
        .eq("id", frameworkId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error("Update ISO framework error:", error);
      throw new Error(error.message || "Failed to update ISO framework");
    }
  },

  // Delete ISO framework
  async deleteISOFramework(frameworkId) {
    try {
      const { error } = await supabase
        .from("iso_frameworks")
        .delete()
        .eq("id", frameworkId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error("Delete ISO framework error:", error);
      throw new Error(error.message || "Failed to delete ISO framework");
    }
  },

  // Get ISO standards — uses dedicated lookup table (1 lightweight query)
  async getISOStandards() {
    try {
      const { data, error } = await supabase
        .from("iso_frameworks")
        .select("id, name, code, is_active")
        .eq("is_active", true)
        .order("name");

      if (error || !data?.length) {
        return this.getDefaultISOStandards();
      }

      // Format to match expected shape for filter dropdowns
      return data.map((fw, index) => ({
        id: fw.id,
        name: fw.name,
        slug: fw.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, ""),
        is_active: true,
        sort_order: index + 1,
      }));
    } catch (error) {
      console.error("Error fetching ISO standards:", error);
      return this.getDefaultISOStandards();
    }
  },

  // Get default ISO standards (fallback)
  getDefaultISOStandards() {
    return [
      {
        id: "1",
        name: "ISO 9001",
        slug: "iso-9001",
        is_active: true,
        sort_order: 1,
      },
      {
        id: "2",
        name: "ISO 14001",
        slug: "iso-14001",
        is_active: true,
        sort_order: 2,
      },
      {
        id: "3",
        name: "ISO 27001",
        slug: "iso-27001",
        is_active: true,
        sort_order: 3,
      },
      {
        id: "4",
        name: "ISO 45001",
        slug: "iso-45001",
        is_active: true,
        sort_order: 4,
      },
      {
        id: "5",
        name: "ISO 22000",
        slug: "iso-22000",
        is_active: true,
        sort_order: 5,
      },
      {
        id: "6",
        name: "ISO 42001",
        slug: "iso-42001",
        is_active: true,
        sort_order: 6,
      },
    ];
  },

  // Get difficulty levels
  getDifficultyLevels() {
    return [
      { name: "Foundation", value: "beginner" },
      { name: "Practitioner", value: "intermediate" },
      { name: "Lead Auditor", value: "advanced" },
    ];
  },

  // Get course reviews/testimonials
  async getCourseReviews(courseId, limit = 10) {
    try {
      const { data: reviews, error } = await supabase
        .from("course_reviews")
        .select("*")
        .eq("course_id", courseId)
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) throw error;

      if (reviews && reviews.length > 0) {
        // Reviewer names come from the public card view (06-P2-17).
        const profileMap = await fetchProfileCards(
          reviews.map((r) => r.user_id),
        );

        return reviews.map((r) => ({
          ...r,
          user_name: profileMap.get(r.user_id)?.full_name || "Anonymous User",
          avatar_url: profileMap.get(r.user_id)?.avatar_url,
          date: new Date(r.created_at).toISOString().split("T")[0], // Format YYYY-MM-DD
        }));
      }

      return [];
    } catch (error) {
      console.error("Get course reviews error:", error);
      throw new Error("Failed to fetch course reviews");
    }
  },

  // Submit a review
  async submitCourseReview({ courseId, userId, rating, comment, userInfo }) {
    try {
      const shouldUpdateFullName =
        userInfo?.full_name &&
        !isCertificateNameMissingOrPlaceholder(
          userInfo.full_name,
          userInfo.email,
        );
      const shouldUpdateAvatar = Boolean(userInfo?.avatar_url);

      if (shouldUpdateFullName || shouldUpdateAvatar) {
        const profileUpdates = {
          id: userId,
          updated_at: new Date().toISOString(),
        };

        if (shouldUpdateFullName) {
          profileUpdates.full_name = userInfo.full_name;
        }

        if (shouldUpdateAvatar) {
          profileUpdates.avatar_url = userInfo.avatar_url;
        }

        try {
          await supabase
            .from("user_profiles")
            .upsert(profileUpdates, { onConflict: "id" });
        } catch (profileErr) {
          console.warn("Failed to update review profile details:", profileErr);
        }
      }

      // 1. Submit the review
      const { data, error } = await supabase
        .from("course_reviews")
        .upsert(
          {
            course_id: courseId,
            user_id: userId,
            rating,
            comment,
            created_at: new Date().toISOString(),
          },
          { onConflict: "course_id,user_id" },
        )
        .select()
        .single();

      if (error) throw error;

      // 2. Fetch Aggregated Stats to update parent Course (Handled by DB Trigger now)

      return data;
    } catch (error) {
      console.error("Submit review error:", error);
      throw error;
    }
  },

  // Get unique list of course level tags (Foundation, Internal Auditor, Lead Auditor etc)
  async getCourseFilterOptions() {
    if (
      _courseFilterOptionsCache &&
      Date.now() - _courseFilterOptionsCacheTime < FILTER_OPTIONS_CACHE_TTL
    ) {
      return _courseFilterOptionsCache;
    }

    try {
      const { data, error } = await supabase
        .from("courses")
        .select("course_types, type_of_course")
        .eq("status", "published")
        .eq("is_active", true);

      if (error) throw error;

      _courseFilterOptionsCache = buildCourseFilterOptions(data || []);
      _courseFilterOptionsCacheTime = Date.now();
      return _courseFilterOptionsCache;
    } catch (error) {
      console.error("Error fetching course filter options:", error);
      return { courseTypes: [], deliveryTypes: [] };
    }
  },

  async getUniqueCourseTypes() {
    const options = await this.getCourseFilterOptions();
    return options.courseTypes;
  },

  // Delete a review
  async deleteCourseReview(courseId, userId) {
    try {
      const { error } = await supabase
        .from("course_reviews")
        .delete()
        .eq("course_id", courseId)
        .eq("user_id", userId);

      if (error) throw error;

      // Update Aggregates (Handled by DB Trigger now)

      return true;
    } catch (error) {
      console.error("Delete review error:", error);
      throw error;
    }
  },

  // Get user's existing review for a course
  async getUserReview(courseId, userId) {
    try {
      const { data, error } = await supabase
        .from("course_reviews")
        .select("*")
        .eq("course_id", courseId)
        .eq("user_id", userId)
        .maybeSingle();

      if (error) return null;
      return data;
    } catch (error) {
      return null;
    }
  },

  // ===== ADMIN OPERATIONS =====

  // Helper function to ensure ISO frameworks exist in the database
  async ensureISOFrameworksExist(isoFrameworks) {
    if (
      !isoFrameworks ||
      !Array.isArray(isoFrameworks) ||
      isoFrameworks.length === 0
    ) {
      return;
    }

    try {
      // Get existing frameworks from database
      const { data: existingFrameworks, error: fetchError } = await supabase
        .from("iso_frameworks")
        .select("name")
        .in("name", isoFrameworks);

      if (fetchError) throw fetchError;

      // Find frameworks that don't exist
      const existingNames = existingFrameworks?.map((f) => f.name) || [];
      const newFrameworks = isoFrameworks.filter(
        (name) => !existingNames.includes(name),
      );

      // Create new frameworks
      if (newFrameworks.length > 0) {
        const frameworksToInsert = newFrameworks.map((name) => {
          // Extract ISO code from name (e.g., "ISO 42001" -> "42001")
          const codeMatch = name.match(/ISO\s*(\d+)/i);
          const code = codeMatch
            ? codeMatch[1]
            : name.replace(/[^0-9]/g, "") || "CUSTOM";

          return {
            name: name,
            code: code,
            description: `${name} - Automatically created from course`,
            is_active: true,
          };
        });

        const { error: insertError } = await supabase
          .from("iso_frameworks")
          .insert(frameworksToInsert);

        if (insertError) throw insertError;
      }
    } catch (error) {
      throw new Error(`Failed to validate ISO frameworks: ${error.message}`);
    }
  },

  // Create new course (Admin only)
  async createCourse(courseData) {
    try {
      // Ensure ISO frameworks exist in database before creating course
      await this.ensureISOFrameworksExist(courseData.iso_frameworks);

      // Get current user to set as instructor
      const user = await authService.getCurrentUser();
      if (!user) {
        throw new Error("User not authenticated");
      }

      // Validate category_id if provided
      let validCategoryId = null;
      if (courseData.category_id) {
        const { data: category, error: categoryError } = await supabase
          .from("course_categories")
          .select("id")
          .eq("id", courseData.category_id)
          .single();

        if (!categoryError && category) {
          validCategoryId = courseData.category_id;
        } else {
          throw new Error("Selected course category no longer exists");
        }
      }

      const status =
        courseData.status || (courseData.is_active ? "published" : "draft");
      const validStatuses = ["draft", "published", "archived"];
      if (!validStatuses.includes(status)) {
        throw new Error("Invalid course status");
      }
      const isActive =
        courseData.is_active !== undefined
          ? Boolean(courseData.is_active)
          : status === "published";

      const coursePayload = withCourseAccreditationRule({
        title: courseData.title,
        description: courseData.description,
        description_virtual: courseData.description_virtual || null,
        description_in_person: courseData.description_in_person || null,
        short_description: courseData.short_description,
        price_inr: parseFloat(courseData.price_inr) || 0,
        price_usd: parseFloat(courseData.price_usd) || 0,
        price_cad: parseFloat(courseData.price_cad) || 0,
        non_exemplar_price_inr:
          parseFloat(courseData.non_exemplar_price_inr) || 0,
        non_exemplar_price_usd:
          parseFloat(courseData.non_exemplar_price_usd) || 0,
        non_exemplar_price_cad:
          parseFloat(courseData.non_exemplar_price_cad) || 0,
        batch_price_inr: parseFloat(courseData.batch_price_inr) || 0,
        batch_price_usd: parseFloat(courseData.batch_price_usd) || 0,
        batch_price_cad: parseFloat(courseData.batch_price_cad) || 0,
        batch_non_exemplar_price_inr:
          parseFloat(courseData.batch_non_exemplar_price_inr) || 0,
        batch_non_exemplar_price_usd:
          parseFloat(courseData.batch_non_exemplar_price_usd) || 0,
        batch_non_exemplar_price_cad:
          parseFloat(courseData.batch_non_exemplar_price_cad) || 0,
        in_person_batch_price_inr:
          parseFloat(courseData.in_person_batch_price_inr) || 0,
        in_person_batch_price_usd:
          parseFloat(courseData.in_person_batch_price_usd) || 0,
        in_person_batch_price_cad:
          parseFloat(courseData.in_person_batch_price_cad) || 0,
        in_person_batch_non_exemplar_price_inr:
          parseFloat(courseData.in_person_batch_non_exemplar_price_inr) || 0,
        in_person_batch_non_exemplar_price_usd:
          parseFloat(courseData.in_person_batch_non_exemplar_price_usd) || 0,
        in_person_batch_non_exemplar_price_cad:
          parseFloat(courseData.in_person_batch_non_exemplar_price_cad) || 0,
        duration_hours: parseInt(courseData.duration_hours) || 0,
        credits: parseFloat(courseData.credits) || 0,
        difficulty_level: courseData.difficulty_level || "beginner",
        language: courseData.language || "english",
        max_students: courseData.max_students
          ? parseInt(courseData.max_students)
          : null,
        category_id: validCategoryId,
        prerequisites: courseData.prerequisites?.filter(Boolean) || [],
        learning_outcomes:
          courseData.learning_objectives?.filter(Boolean) || [],
        tags: courseData.tags?.filter(Boolean) || [],
        iso_frameworks: courseData.iso_frameworks?.filter(Boolean) || [],
        course_categories: courseData.course_categories?.filter(Boolean) || [],
        course_types: courseData.course_types?.filter(Boolean) || [],
        type_of_course: courseData.type_of_course?.filter(Boolean) || [],
        thumbnail_url: courseData.thumbnail_url || null,
        ai_knowledge_base: courseData.ai_knowledge_base || null,
        status,
        is_active: isActive,
        instructor_id: courseData.instructor_id || user.id,
        is_featured: courseData.featured || false,
        certification_provided: courseData.certificate_enabled || false,
        certificate_validity_days: courseData.certificate_validity_days ?? null,
        accredited_available: courseData.accredited_available ?? true,
        non_accredited_available: courseData.non_accredited_available ?? true,
      });

      // First, try to create the course without the relationship query
      const { data, error } = await supabase
        .from("courses")
        .insert([coursePayload])
        .select()
        .single();

      if (error) throw error;

      // Then fetch the course with relationships
      const { data: courseWithRelations, error: fetchError } = await supabase
        .from("courses")
        .select(
          `
          *,
          category:course_categories(
            id,
            name,
            slug
          )
        `,
        )
        .eq("id", data.id)
        .single();

      if (fetchError) {
        return data; // Return basic course data if relationship fetch fails
      }

      return courseWithRelations;
    } catch (error) {
      console.error("Create course error:", error);
      throw new Error(`Failed to create course: ${error.message}`);
    }
  },

  // Update course (Admin only)
  async updateCourse(courseId, courseData, expectedUpdatedAt = null) {
    try {
      // Ensure ISO frameworks exist in database before updating course
      const isoFrameworks =
        courseData.iso_frameworks || courseData.isoFrameworks;
      if (isoFrameworks) {
        await this.ensureISOFrameworksExist(isoFrameworks);
      }

      const updateData = {
        updated_at: new Date().toISOString().replace(/\.\d{3}/, ""), // Remove milliseconds
      };

      // Only include fields that are actually provided in courseData
      if (courseData.title !== undefined) updateData.title = courseData.title;
      if (courseData.description !== undefined)
        updateData.description = courseData.description;
      if (courseData.description_virtual !== undefined)
        updateData.description_virtual = courseData.description_virtual;
      if (courseData.description_in_person !== undefined)
        updateData.description_in_person = courseData.description_in_person;
      if (courseData.short_description !== undefined)
        updateData.short_description = courseData.short_description;
      // Handle dual currency pricing
      if (courseData.price_inr !== undefined) {
        updateData.price_inr = parseFloat(courseData.price_inr) || 0;
        // Also update the legacy price field for backward compatibility
        updateData.price = parseFloat(courseData.price_inr) || 0;
      }
      if (courseData.price_usd !== undefined) {
        updateData.price_usd = parseFloat(courseData.price_usd) || 0;
      }
      if (courseData.price_cad !== undefined) {
        updateData.price_cad = parseFloat(courseData.price_cad) || 0;
      }
      if (courseData.non_exemplar_price_inr !== undefined) {
        updateData.non_exemplar_price_inr =
          parseFloat(courseData.non_exemplar_price_inr) || 0;
      }
      if (courseData.non_exemplar_price_usd !== undefined) {
        updateData.non_exemplar_price_usd =
          parseFloat(courseData.non_exemplar_price_usd) || 0;
      }
      if (courseData.non_exemplar_price_cad !== undefined) {
        updateData.non_exemplar_price_cad =
          parseFloat(courseData.non_exemplar_price_cad) || 0;
      }
      if (courseData.batch_price_inr !== undefined) {
        updateData.batch_price_inr =
          parseFloat(courseData.batch_price_inr) || 0;
      }
      if (courseData.batch_price_usd !== undefined) {
        updateData.batch_price_usd =
          parseFloat(courseData.batch_price_usd) || 0;
      }
      if (courseData.batch_price_cad !== undefined) {
        updateData.batch_price_cad =
          parseFloat(courseData.batch_price_cad) || 0;
      }
      if (courseData.batch_non_exemplar_price_inr !== undefined) {
        updateData.batch_non_exemplar_price_inr =
          parseFloat(courseData.batch_non_exemplar_price_inr) || 0;
      }
      if (courseData.batch_non_exemplar_price_usd !== undefined) {
        updateData.batch_non_exemplar_price_usd =
          parseFloat(courseData.batch_non_exemplar_price_usd) || 0;
      }
      if (courseData.batch_non_exemplar_price_cad !== undefined) {
        updateData.batch_non_exemplar_price_cad =
          parseFloat(courseData.batch_non_exemplar_price_cad) || 0;
      }
      if (courseData.in_person_batch_price_inr !== undefined) {
        updateData.in_person_batch_price_inr =
          parseFloat(courseData.in_person_batch_price_inr) || 0;
      }
      if (courseData.in_person_batch_price_usd !== undefined) {
        updateData.in_person_batch_price_usd =
          parseFloat(courseData.in_person_batch_price_usd) || 0;
      }
      if (courseData.in_person_batch_price_cad !== undefined) {
        updateData.in_person_batch_price_cad =
          parseFloat(courseData.in_person_batch_price_cad) || 0;
      }
      if (courseData.in_person_batch_non_exemplar_price_inr !== undefined) {
        updateData.in_person_batch_non_exemplar_price_inr =
          parseFloat(courseData.in_person_batch_non_exemplar_price_inr) || 0;
      }
      if (courseData.in_person_batch_non_exemplar_price_usd !== undefined) {
        updateData.in_person_batch_non_exemplar_price_usd =
          parseFloat(courseData.in_person_batch_non_exemplar_price_usd) || 0;
      }
      if (courseData.in_person_batch_non_exemplar_price_cad !== undefined) {
        updateData.in_person_batch_non_exemplar_price_cad =
          parseFloat(courseData.in_person_batch_non_exemplar_price_cad) || 0;
      }
      if (courseData.duration_hours !== undefined)
        updateData.duration_hours = parseInt(courseData.duration_hours) || 0;
      if (courseData.credits !== undefined)
        updateData.credits = parseFloat(courseData.credits) || 0;
      if (courseData.difficulty_level !== undefined)
        updateData.difficulty_level = courseData.difficulty_level;
      if (courseData.language !== undefined)
        updateData.language = courseData.language;
      if (courseData.max_students !== undefined)
        updateData.max_students = courseData.max_students
          ? parseInt(courseData.max_students)
          : null;
      if (courseData.category_id !== undefined)
        updateData.category_id = courseData.category_id || null;
      if (courseData.prerequisites !== undefined)
        updateData.prerequisites =
          courseData.prerequisites?.filter(Boolean) || [];
      if (courseData.learning_objectives !== undefined)
        updateData.learning_outcomes =
          courseData.learning_objectives?.filter(Boolean) || [];
      if (courseData.tags !== undefined)
        updateData.tags = courseData.tags?.filter(Boolean) || [];

      // Handle TagInput fields - only update if explicitly provided
      if (courseData.iso_frameworks !== undefined) {
        updateData.iso_frameworks =
          courseData.iso_frameworks ||
          courseData.isoFrameworks?.filter(Boolean) ||
          [];
      }
      if (courseData.course_categories !== undefined) {
        updateData.course_categories =
          courseData.course_categories ||
          courseData.courseCategories?.filter(Boolean) ||
          [];
      }
      if (courseData.course_types !== undefined) {
        updateData.course_types =
          courseData.course_types ||
          courseData.courseTypes?.filter(Boolean) ||
          [];
      }
      if (courseData.type_of_course !== undefined) {
        updateData.type_of_course =
          courseData.type_of_course ||
          courseData.typeOfCourse?.filter(Boolean) ||
          [];
      }

      if (courseData.featured !== undefined)
        updateData.is_featured = courseData.featured || false;
      if (courseData.certificate_enabled !== undefined)
        updateData.certification_provided =
          courseData.certificate_enabled || false;
      if (courseData.certificate_validity_days !== undefined)
        updateData.certificate_validity_days =
          courseData.certificate_validity_days ?? null;
      const normalizedCourseData = withCourseAccreditationRule(courseData);
      if (normalizedCourseData.accredited_available !== undefined)
        updateData.accredited_available =
          normalizedCourseData.accredited_available ?? true;
      if (normalizedCourseData.non_accredited_available !== undefined)
        updateData.non_accredited_available =
          normalizedCourseData.non_accredited_available ?? true;
      if (courseData.status !== undefined) {
        const validStatuses = ["draft", "published", "archived"];
        if (!validStatuses.includes(courseData.status)) {
          throw new Error("Invalid course status");
        }
        updateData.status = courseData.status;
      }
      if (courseData.is_active !== undefined) {
        updateData.is_active = Boolean(courseData.is_active);
      } else if (courseData.status !== undefined) {
        updateData.is_active = courseData.status === "published";
      }

      // Only update thumbnail if provided
      if (courseData.thumbnail_url !== undefined) {
        updateData.thumbnail_url = courseData.thumbnail_url;
      }

      if (courseData.ai_knowledge_base !== undefined) {
        // The database moves this into course_ai_knowledge; "" clears it.
        updateData.ai_knowledge_base = courseData.ai_knowledge_base || "";
      }

      let updateQuery = supabase
        .from("courses")
        .update(updateData)
        .eq("id", courseId);
      if (expectedUpdatedAt) {
        updateQuery = updateQuery.eq("updated_at", expectedUpdatedAt);
      }
      const { data, error } = await updateQuery
        .select(
          `
          *,
          category:course_categories(
            id,
            name,
            slug
          )
        `,
        )
        .single();

      if (error) throw getCourseUpdateError(error, expectedUpdatedAt);
      return data;
    } catch (error) {
      console.error("Update course error:", error);
      throw new Error(error?.message || "Failed to update course");
    }
  },

  // Check for dependencies (transactions, enrollments) without deleting
  async checkDependencies(courseId) {
    try {
      return await createCourseDeletionService(supabase).inspect(courseId);
    } catch (error) {
      console.error("Check dependencies error:", error);
      throw new Error(error.message || "Failed to check dependencies");
    }
  },

  // Delete course (Admin only)
  async deleteCourse(courseId, force = false) {
    try {
      const result = await createCourseDeletionService(supabase).remove(
        courseId,
        { force },
      );
      if (result.deleted && result.storage_urls?.length > 0) {
        try {
          await lessonService.deleteStorageFiles(result.storage_urls);
        } catch (storageError) {
          result.warnings = [
            `Course deleted, but storage cleanup failed: ${storageError.message}`,
          ];
        }
      }
      return result;
    } catch (error) {
      console.error("Delete course error:", error);
      // Keep the server's code and hint: a refusal to destroy history (23514)
      // tells the admin to archive instead.
      throw Object.assign(
        new Error(error.message || "Failed to delete course"),
        {
          code: error.code,
          hint: error.hint,
          details: error.details,
        },
      );
    }
  },

  // Update course status (Admin only)
  async updateCourseStatus(courseId, status) {
    try {
      const validStatuses = ["draft", "published", "archived"];
      if (!validStatuses.includes(status)) {
        throw new Error("Invalid course status");
      }
      const isActive = status === "published";

      const { data, error } = await supabase
        .from("courses")
        .update({
          status,
          is_active: isActive,
          updated_at: new Date().toISOString().replace(/\.\d{3}/, ""), // Remove milliseconds
        })
        .eq("id", courseId)
        .select()
        .single();

      if (error) throw error;

      // Queue notification if status is published or archived
      if (data && (status === "published" || status === "archived")) {
        try {
          const { data: creator } = await supabase
            .from("user_profiles")
            .select("id, email, full_name")
            .eq("id", data.created_by)
            .single();

          if (creator?.email) {
            await notificationQueueService.queueNotification({
              recipientEmail: creator.email,
              recipientName: creator.full_name,
              recipientUserId: data.created_by,
              notificationType: "course_status_changed",
              // Courses carry no platform; brand the email for the site the
              // admin acted on (detectPlatform accepts the platform key).
              platform: getCurrentPlatform(),
              data: {
                courseName: data.title,
                status,
                creatorName: creator.full_name,
              },
            });
          }
        } catch (err) {
          console.error(
            "Failed to queue course_status_changed notification:",
            err,
          );
        }
      }

      return data;
    } catch (error) {
      console.error("Update course status error:", error);
      throw new Error(error.message || "Failed to update course status");
    }
  },

  // Bulk operations (Admin only)
  async bulkUpdateCourses(courseIds, updates) {
    try {
      const updateData = {
        ...updates,
        updated_at: new Date().toISOString().replace(/\.\d{3}/, ""), // Remove milliseconds
      };

      const { data, error } = await supabase
        .from("courses")
        .update(updateData)
        .in("id", courseIds)
        .select();

      if (error) throw error;
      return ensureAffectedRows(courseIds, data, "Updated");
    } catch (error) {
      console.error("Bulk update courses error:", error);
      throw new Error(error.message || "Failed to bulk update courses");
    }
  },

  async bulkChangeCourseStatus(courseIds, status) {
    return createCourseBulkMutationService(supabase).changeStatus(
      courseIds,
      status,
    );
  },

  async bulkDeleteCourses(courseIds) {
    return createCourseBulkMutationService(supabase, (values) =>
      uploadService.deleteStorageReferences(values),
    ).remove(courseIds);
  },

  // Get existing tags from all courses (legacy support)
  async getExistingTags() {
    try {
      const { data, error } = await supabase
        .from("courses")
        .select(
          "tags, iso_frameworks, course_categories, course_types, type_of_course",
        )
        .not("tags", "is", null);

      if (error) throw error;

      // Extract all unique tags from all courses and all tag categories
      const allTags = new Set();
      data?.forEach((course) => {
        // Legacy tags field
        if (Array.isArray(course.tags)) {
          course.tags.forEach((tag) => {
            if (tag && tag.trim()) {
              allTags.add(tag.trim());
            }
          });
        }

        // New separate tag fields
        [
          course.iso_frameworks,
          course.course_categories,
          course.course_types,
          course.type_of_course,
        ].forEach((tags) => {
          if (Array.isArray(tags)) {
            tags.forEach((tag) => {
              if (tag && tag.trim()) {
                allTags.add(tag.trim());
              }
            });
          }
        });
      });

      return Array.from(allTags).sort();
    } catch (error) {
      console.error("Error getting existing tags:", error);
      return [];
    }
  },

  // Get existing ISO framework tags
  async getExistingISOFrameworks() {
    try {
      // First try to get from dedicated iso_frameworks table
      const { data: frameworkData, error: frameworkError } = await supabase
        .from("iso_frameworks")
        .select("name")
        .eq("is_active", true)
        .order("name");

      if (!frameworkError && frameworkData?.length > 0) {
        return frameworkData.map((item) => item.name);
      }

      // Fallback to courses table if dedicated table is empty
      const { data, error } = await supabase
        .from("courses")
        .select("iso_frameworks")
        .not("iso_frameworks", "is", null);

      if (error) throw error;

      const tags = new Set();
      data?.forEach((course) => {
        if (Array.isArray(course.iso_frameworks)) {
          course.iso_frameworks.forEach((tag) => {
            if (tag && tag.trim()) {
              tags.add(tag.trim());
            }
          });
        }
      });

      return Array.from(tags).sort();
    } catch (error) {
      console.error("Error getting ISO framework tags:", error);
      return [];
    }
  },

  // Get existing course category tags
  async getExistingCourseCategories() {
    try {
      // First try to get from dedicated course_categories table
      const { data: categoryData, error: categoryError } = await supabase
        .from("course_categories")
        .select("name")
        .eq("is_active", true)
        .order("name");

      if (!categoryError && categoryData?.length > 0) {
        return categoryData.map((item) => item.name);
      }

      // Fallback to courses table if dedicated table is empty
      const { data, error } = await supabase
        .from("courses")
        .select("course_categories")
        .not("course_categories", "is", null);

      if (error) throw error;

      const tags = new Set();
      data?.forEach((course) => {
        if (Array.isArray(course.course_categories)) {
          course.course_categories.forEach((tag) => {
            if (tag && tag.trim()) {
              tags.add(tag.trim());
            }
          });
        }
      });

      return Array.from(tags).sort();
    } catch (error) {
      console.error("Error getting course category tags:", error);
      return [];
    }
  },

  // Get existing course type tags
  async getExistingCourseTypes() {
    try {
      // First try to get from dedicated course_types table
      const { data: courseTypes, error: courseTypesError } = await supabase
        .from("course_types")
        .select("name")
        .eq("is_active", true)
        .order("name");

      if (!courseTypesError && courseTypes && courseTypes.length > 0) {
        return courseTypes.map((ct) => ct.name);
      }

      // Fallback to courses table if dedicated table is empty
      const { data, error } = await supabase
        .from("courses")
        .select("course_types")
        .not("course_types", "is", null);

      if (error) throw error;

      const tags = new Set();
      data?.forEach((course) => {
        if (Array.isArray(course.course_types)) {
          course.course_types.forEach((tag) => {
            if (tag && tag.trim()) {
              tags.add(tag.trim());
            }
          });
        }
      });

      return Array.from(tags).sort();
    } catch (error) {
      console.error("Error getting course type tags:", error);
      return [];
    }
  },

  // Get existing type of course tags
  async getExistingTypeOfCourse() {
    const options = await this.getCourseFilterOptions();
    return options.deliveryTypes;
  },

  // Get analytics summary (Admin only)
  async getAnalyticsSummary(options = {}) {
    return analyticsService.getReportingOverview(options);
  },
};

export default courseService;

// ===== LESSON SERVICE =====

const EMPTY_GUIDED_LESSON_DATA = {
  version: 1,
  blocks: [],
  completion: { required_block_ids: [] },
};

export const lessonService = {
  async copyLessonsToMode({ courseId, sourceMode, targetMode, lessonIds }) {
    const { data, error } = await supabase.rpc("copy_lessons_to_mode", {
      p_course_id: courseId,
      p_source_mode: sourceMode,
      p_target_mode: targetMode,
      p_lesson_ids: lessonIds,
    });
    if (error) throw error;
    return data;
  },

  async getLessonsByCourseId(courseId, deliveryMode = null) {
    try {
      let query = supabase
        .from("lessons")
        .select("*")
        .eq("course_id", courseId)
        .order("order_index", { ascending: true })
        .order("id", { ascending: true });
      if (deliveryMode) query = query.eq("delivery_mode", deliveryMode);
      const { data, error } = await query;
      if (error) throw error;
      return await withQuizAnswerKeys(data);
    } catch (error) {
      throw new Error(`Failed to fetch lessons: ${error.message}`);
    }
  },

  async createLesson(lessonData) {
    try {
      const deliveryMode = lessonData.delivery_mode || "self_paced";
      const moduleId = await resolveLessonModuleId(
        {
          moduleId: lessonData.module_id,
          courseId: lessonData.course_id,
          deliveryMode,
        },
        {
          async findModules(courseId, mode) {
            const { data, error } = await supabase
              .from("course_modules")
              .select("id")
              .eq("course_id", courseId)
              .eq("delivery_mode", mode)
              .order("sort_order", { ascending: true })
              .limit(1);
            if (error) throw error;
            return data || [];
          },
          async createModule(payload) {
            const { data, error } = await supabase
              .from("course_modules")
              .insert([payload])
              .select()
              .single();
            if (error) throw error;
            return data;
          },
        },
      );

      const mappedType = lessonData.type || "text";
      // Keys travel with the questions in the same INSERT: the
      // strip_lesson_quiz_answer_key trigger stores them in
      // lesson_quiz_answer_keys and strips them from quiz_data atomically.
      const { answerKeys } = splitQuizAnswerKeys(lessonData.quiz_data);

      const lessonPayload = {
        ...lessonData,
        module_id: moduleId,
        delivery_mode: deliveryMode,
        type: mappedType,
        scorm_package_url: lessonData.scorm_package_url || null,
        scorm_manifest_data:
          lessonData.scorm_metadata || lessonData.scorm_manifest_data || null,
        mindsmith_iframe_url: lessonData.mindsmith_iframe_url || null,
        code_content: lessonData.code_content || null,
        code_language: lessonData.code_language || "javascript",
        quiz_data: lessonData.quiz_data || null,
        guided_data: lessonData.guided_data || EMPTY_GUIDED_LESSON_DATA,
        document_metadata: lessonData.document_metadata || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      delete lessonPayload.lesson_type;
      delete lessonPayload.scorm_metadata;

      const { data, error } = await supabase
        .from("lessons")
        .insert([lessonPayload])
        .select()
        .single();

      if (error) throw error;

      if (Array.isArray(lessonData.quiz_data?.questions)) {
        return {
          ...data,
          quiz_data: mergeQuizAnswerKeys(data.quiz_data, answerKeys),
        };
      }

      return data;
    } catch (error) {
      console.error("Lesson creation failed:", error);
      throw new Error(`Failed to create lesson: ${error.message}`);
    }
  },

  // expectedUpdatedAt: the lesson's updated_at when the editor loaded it. The
  // UPDATE only matches that version, so a save never silently overwrites a
  // newer edit from another tab or editor (same pattern as updateCourse).
  async updateLesson(id, lessonData, expectedUpdatedAt = null) {
    try {
      const mappedType = lessonData.type || "text";
      const quizProvided = Array.isArray(lessonData.quiz_data?.questions);
      // Keys travel with the questions in the same UPDATE: the
      // strip_lesson_quiz_answer_key trigger stores them in
      // lesson_quiz_answer_keys and strips them from quiz_data atomically, so
      // questions and keys can never be out of step.
      const { answerKeys } = splitQuizAnswerKeys(lessonData.quiz_data);

      const updatePayload = {
        ...lessonData,
        type: mappedType,
        updated_at: new Date().toISOString(),
        ...(quizProvided ? { quiz_data: lessonData.quiz_data } : {}),
      };

      const preserveUnlessProvided = [
        "scorm_package_url",
        "scorm_manifest_data",
        "mindsmith_iframe_url",
        "code_content",
        "code_language",
        "quiz_data",
        "guided_data",
        "document_metadata",
      ];
      preserveUnlessProvided.forEach((field) => {
        if (!Object.prototype.hasOwnProperty.call(lessonData, field)) {
          delete updatePayload[field];
        }
      });
      if (
        Object.prototype.hasOwnProperty.call(lessonData, "guided_data") &&
        !lessonData.guided_data
      ) {
        updatePayload.guided_data = EMPTY_GUIDED_LESSON_DATA;
      }
      if (
        Object.prototype.hasOwnProperty.call(lessonData, "scorm_metadata") &&
        !Object.prototype.hasOwnProperty.call(lessonData, "scorm_manifest_data")
      ) {
        updatePayload.scorm_manifest_data = lessonData.scorm_metadata;
      }

      delete updatePayload.lesson_type;
      delete updatePayload.scorm_metadata;

      let updateQuery = supabase
        .from("lessons")
        .update(updatePayload)
        .eq("id", id);
      if (expectedUpdatedAt) {
        updateQuery = updateQuery.eq("updated_at", expectedUpdatedAt);
      }
      const { data, error } = await updateQuery.select().single();

      if (error) {
        if (expectedUpdatedAt && error?.code === "PGRST116") {
          throw new Error(
            "This lesson changed in another editor or tab. Reload the course before saving again.",
          );
        }
        throw error;
      }

      if (quizProvided) {
        return {
          ...data,
          quiz_data: mergeQuizAnswerKeys(data.quiz_data, answerKeys),
        };
      }

      return data;
    } catch (error) {
      throw new Error(`Failed to update lesson: ${error.message}`);
    }
  },

  async deleteStorageFiles(urls) {
    await uploadService.deleteStorageReferences(urls || []);
  },

  async cleanupUnusedStorage(candidates) {
    if (!candidates?.length) return [];
    const { data, error } = await supabase.rpc(
      "get_safe_storage_cleanup_candidates",
      { p_candidates: candidates },
    );
    if (error) throw error;
    const safeCandidates = Array.isArray(data) ? data : [];
    await uploadService.deleteStorageReferences(safeCandidates);
    return safeCandidates;
  },

  async updateLessonOrder(lessons) {
    try {
      return await lessonOrderService.save(lessons);
    } catch (error) {
      console.error("Failed to update lesson order:", error);
      throw new Error(`Failed to update lesson order: ${error.message}`);
    }
  },

  async deleteLesson(id) {
    try {
      return await createLessonMutationService(supabase, (urls) =>
        this.deleteStorageFiles(urls),
      ).delete(id);
    } catch (error) {
      console.error("Delete lesson error:", error);
      throw new Error(`Failed to delete lesson: ${error.message}`);
    }
  },
};

// ===== NAMED EXPORTS =====

export const getCourses = (...args) => courseService.getCourses(...args);
export const deleteCourse = (...args) => courseService.deleteCourse(...args);

export async function duplicateCourse(courseId) {
  try {
    return await duplicateCourseWithContent(courseId, {
      loadCourse: async (id) => {
        const course = await courseService.getCourseById(id, {
          includeEnrollmentStats: false,
        });
        if (!course) throw new Error("Course not found");
        // Carry the assistant knowledge into the copy (the insert routes it
        // into course_ai_knowledge).
        const aiKnowledgeBase = await courseService
          .getCourseAiKnowledge(id)
          .catch(() => "");
        return aiKnowledgeBase
          ? { ...course, ai_knowledge_base: aiKnowledgeBase }
          : course;
      },
      createCourse: async (payload) => {
        const { data, error } = await supabase
          .from("courses")
          .insert([payload])
          .select()
          .single();
        if (error) throw error;
        return data;
      },
      loadModules: async (id) => {
        const { data, error } = await supabase
          .from("course_modules")
          .select("*")
          .eq("course_id", id)
          .order("sort_order", { ascending: true });
        if (error) throw error;
        return data || [];
      },
      createModule: async (payload) => {
        const { data, error } = await supabase
          .from("course_modules")
          .insert([payload])
          .select()
          .single();
        if (error) throw error;
        return data;
      },
      loadLessons: async (id) => {
        const { data, error } = await supabase
          .from("lessons")
          .select("*")
          .eq("course_id", id)
          .order("delivery_mode", { ascending: true })
          .order("order_index", { ascending: true })
          .order("id", { ascending: true });
        if (error) throw error;
        // Answer keys travel with the copy; the insert below strips them out
        // of quiz_data again and files them under the new lesson id.
        return await withQuizAnswerKeys(data || []);
      },
      createLesson: async (payload) => {
        // Keys travel with the questions; the strip_lesson_quiz_answer_key
        // trigger files them under the new lesson id and strips them from
        // quiz_data in the same INSERT.
        const { data, error } = await supabase
          .from("lessons")
          .insert([payload])
          .select()
          .single();
        if (error) throw error;
        return data;
      },
      loadDeliveryModes: async (id) => {
        const { data, error } = await supabase
          .from("course_delivery_modes")
          .select("*")
          .eq("course_id", id);
        if (error) throw error;
        return data || [];
      },
      createDeliveryMode: async (payload) => {
        const { data, error } = await supabase
          .from("course_delivery_modes")
          .upsert([payload], { onConflict: "course_id,delivery_mode" })
          .select()
          .single();
        if (error) throw error;
        return data;
      },
      loadLivePairs: async (id) => {
        const { data, error } = await supabase
          .from("course_live_content_pairs")
          .select("*")
          .eq("course_id", id)
          .order("sort_order", { ascending: true });
        if (error) throw error;
        return data || [];
      },
      createLivePair: async (payload) => {
        const { data, error } = await supabase
          .from("course_live_content_pairs")
          .insert([payload])
          .select()
          .single();
        if (error) throw error;
        return data;
      },
      loadInstructorMaterials: async (id) => {
        const { data, error } = await supabase
          .from("instructor_course_materials")
          .select("*")
          .eq("course_id", id)
          .order("created_at", { ascending: true });
        if (error) throw error;
        return data || [];
      },
      createInstructorMaterial: async (payload) => {
        const { data, error } = await supabase
          .from("instructor_course_materials")
          .insert([payload])
          .select()
          .single();
        if (error) throw error;
        return data;
      },
      loadResources: async (id) => {
        const { data, error } = await supabase
          .from("course_resources")
          .select("*")
          .eq("course_id", id)
          .order("sort_order", { ascending: true });
        if (error) throw error;
        return data || [];
      },
      createResource: async (payload) => {
        const { data, error } = await supabase
          .from("course_resources")
          .insert([payload])
          .select()
          .single();
        if (error) throw error;
        return data;
      },
      cleanupCourse: async (id) => {
        const { error } = await supabase.rpc(
          "force_delete_course_with_cleanup",
          {
            target_course_id: id,
          },
        );
        if (error) throw error;
      },
    });
  } catch (error) {
    throw new Error(`Failed to duplicate course: ${error.message}`);
  }
}

export async function bulkUpdateCourseStatus(courseIds, isActive) {
  try {
    const result = await courseService.bulkChangeCourseStatus(
      courseIds,
      isActive ? "published" : "draft",
    );
    return { success: true, updated: Number(result.updated) };
  } catch (error) {
    throw new Error(`Failed to bulk update courses: ${error.message}`);
  }
}

export async function getCourseAnalytics() {
  try {
    const [coursesRes, activeRes, totalEnrollRes, completedRes] =
      await Promise.all([
        supabase.from("courses").select("*", { count: "exact", head: true }),
        supabase
          .from("course_enrollments")
          .select("*", { count: "exact", head: true })
          .eq("enrollment_status", "active"),
        supabase
          .from("course_enrollments")
          .select("*", { count: "exact", head: true }),
        supabase
          .from("course_enrollments")
          .select("*", { count: "exact", head: true })
          .eq("enrollment_status", "completed"),
      ]);

    const totalCourses = coursesRes.count || 0;
    const activeEnrollments = activeRes.count || 0;
    const totalEnrollments = totalEnrollRes.count || 0;
    const completedEnrollments = completedRes.count || 0;
    const completionRate =
      totalEnrollments > 0
        ? Math.round((completedEnrollments / totalEnrollments) * 100)
        : 0;

    return { totalCourses, activeEnrollments, completionRate };
  } catch (error) {
    throw new Error(`Failed to get course analytics: ${error.message}`);
  }
}
